<script setup>
import { computed } from 'vue';
import { getDirectionBackground, randomColor, formatDateTimeByRegion } from '../../helpers';
import { formatPrice, getLevelDescription, getLevelTitle, getStateDescription, getTopBottomCoordinates, insertStateClasses, lastMarketAnalysis } from './utils';
import PriceTargetRangeMinimal from './PriceTargetRangeMinimal.vue';

const props = defineProps({
    priceTarget: {
        type: Object,
        required: true
    },
    priceTargetsStates: {
        type: Object,
        required: true
    }
});
const stateTitle = computed(() => {
    const state = Object.keys(props.priceTargetsStates).find((state) => state === props.priceTarget.state);

    return state ? props.priceTargetsStates[state] : props.priceTarget.state;
});
</script>
<template>
    <div>
        <div v-if="priceTarget">
            <div class="grid items-center lg:grid-cols-[.5fr_2fr_2fr_1fr_2fr_2fr_1fr_auto_1fr] grid-cols-8 ml-6 gap-4">
                <div class="flex items-center gap-2">
                    <div v-if="priceTarget.market_symbol.logo" class="flex-shrink-0">
                        <img class="w-10 h-10 rounded-xl object-contain" :src="priceTarget.market_symbol.logo" />
                    </div>
                    <div v-else :style="{ backgroundColor: randomColor() }" class="flex items-center justify-center rounded-xl w-12 h-12">
                        <i class="fi fi-rr-apartment text-white text-lg"></i>
                    </div>
                </div>
                <span class="text-xs font-semibold truncate">{{ priceTarget.market_symbol.name }}</span>
                <div class="text-xxs">
                    <span>{{ $t('last_update') }} </span>
                    <br />
                    <span class="font-semibold">{{ formatDateTimeByRegion(priceTarget.updated_at) }}</span>
                </div>
                <div class="align-middle justify-center flex items-center">
                    <div :class="getDirectionBackground(priceTarget?.direction)" class="uppercase px-3 rounded-lg text-xxs font-semibold w-fit">
                        {{ priceTarget?.direction }}
                    </div>
                </div>

                <div class="justify-center flex items-center">
                    <span class="rounded-full text-xxs font-semibold text-center">
                        {{ getLevelTitle(priceTarget.level) }}
                    </span>
                    <info-text :title="getLevelTitle(priceTarget.level)" :text="getLevelDescription(priceTarget.level)" class="hidden md:inline-block ml-1" />
                </div>

                <div class="align-middle justify-center flex items-center">
                    <span :class="insertStateClasses(priceTarget.state)" class="px-3 min-w-28 py-1 rounded-full text-xxs font-semibold w-fit text-center">
                        {{ $translate(stateTitle) }}
                    </span>

                    <info-text :title="$translate(stateTitle)" :text="getStateDescription(priceTarget.state)" class="hidden md:inline-block ml-1" />
                </div>

                <div class="text-xs hidden md:block">
                    <p>Kurs</p>
                    <div class="flex items-center">
                        <i class="fi fi-rr-arrow-trend-up text-xs font-semibold mr-1"></i>
                        <p class="font-semibold">{{ formatPrice(priceTarget.market_symbol.last_price) }}</p>
                    </div>
                </div>
                <div class="text-xs mr-20 md:mr-0 md:hidden">
                    <span class="block font-semibold">
                        <i class="fi fi-rr-arrow-trend-up text-xs font-semibold mr-1"></i>
                        {{ formatPrice(priceTarget.market_symbol.last_price) }}
                    </span>
                </div>

                <PriceTargetRangeMinimal
                    :direction="priceTarget.direction"
                    :last-price="priceTarget.market_symbol.last_price"
                    :min="getTopBottomCoordinates('bottom', priceTarget.min, priceTarget.max)"
                    :max="getTopBottomCoordinates('top', priceTarget.min, priceTarget.max)"
                    class="group"
                />

                <div class="w-full h-full">
                    <router-link
                        :to="lastMarketAnalysis(priceTarget.recent_contentstream)"
                        class="w-full h-full flex items-center justify-center bg-blue-light/50 hover:bg-blue-light rounded-r-xl opacity-100 text-center"
                    >
                        <button class="w-full h-full cursor-pointer rounded-xl p-2 text-xxs font-bold flex items-center justify-center text-dashboard-primary-blue">
                            <span class="flex items-center font-bold gap-1 w-full h-full justify-center">
                                <i class="fi fi-rs-arrow-right text-xs mt-1"></i>
                            </span>
                        </button>
                    </router-link>
                </div>
            </div>
        </div>
    </div>
</template>
